import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Icon from "@/components/icon/Icon";
import { Book_2px, Close_fill, Copyright, DownLoadDisabled_2px, Download2px, Print_2px, Sizes } from "@/components/icon/IconList";
import { mixpanelEvent } from "@/lib/mixpanels/mixpanel";
import { Tabs } from "@bookips/solvook-ui-library";
import { Box, Typography, styled, useTheme } from "@mui/material";
import Link from "next/link";
import { useCallback, useMemo } from "react";
import { getFileExtension } from "./utils/getDownloadFileName";
const tabOptions = {
    view: {
        id: "view",
        label: "열람+출력 (대여)",
        infoText: [
            {
                id: 0,
                icon: Book_2px,
                text: "쏠북 뷰어로 열람 (1년)",
                link: "https://solvookguide.oopy.io/viewer"
            },
            {
                id: 1,
                icon: Print_2px,
                text: "PC 프린트 출력 (1년)",
                link: "https://solvookguide.oopy.io/print-agent-guide"
            },
            {
                id: 2,
                icon: DownLoadDisabled_2px,
                text: "PDF 파일 미제공",
                link: "https://solvookguide.oopy.io/pdf"
            }
        ]
    },
    download: {
        id: "download",
        label: "PDF 다운 (소장)",
        infoText: [
            {
                id: 0,
                icon: Book_2px,
                text: "쏠북 뷰어로 열람 (1년)",
                link: "https://solvookguide.oopy.io/viewer"
            },
            {
                id: 1,
                icon: Print_2px,
                text: "PC 프린트 출력 (1년)",
                link: "https://solvookguide.oopy.io/print-agent-guide"
            },
            {
                id: 2,
                icon: Download2px,
                text: "PDF 파일 다운로드 (소장)",
                link: "https://solvookguide.oopy.io/pdf"
            }
        ]
    }
};
const options = {
    view: "view",
    download: "download",
    all: "all"
};
export const HandoutAllowanceSection = (param)=>{
    let { isBooks , productType , purchaseOptions , originFileInfo  } = param;
    const theme = useTheme();
    const { value , tabsProps , changeTab  } = Tabs.useTabs(purchaseOptions[0]);
    const { isOriginSellable , originalFileName  } = originFileInfo;
    const downloadOption = useMemo(()=>{
        if (!isOriginSellable) {
            return tabOptions.download;
        }
        const isHwpFamily = [
            "hwp",
            "hwpx"
        ].includes(getFileExtension(originalFileName));
        const fileType = isHwpFamily ? "HWP" : "DOC";
        const newInfoText = [
            ...tabOptions.download.infoText
        ];
        newInfoText[2] = {
            ...newInfoText[2],
            text: "".concat(fileType, " 및 PDF 파일 다운로드 (소장)")
        };
        return {
            ...tabOptions.download,
            label: "".concat(fileType, " 다운 (소장)"),
            infoText: newInfoText
        };
    }, [
        isOriginSellable,
        originalFileName
    ]);
    let optionType = null;
    if (purchaseOptions.includes(options.view) && purchaseOptions.includes(options.download)) {
        optionType = options.all;
    } else if (purchaseOptions.includes(options.view)) {
        optionType = options.view;
    } else if (purchaseOptions.includes(options.download)) {
        optionType = options.download;
    } else {
        optionType = null;
    }
    const handleClickTab = useCallback((_, value)=>{
        changeTab(_, value);
        //mx-134
        mixpanelEvent("Tab UI Clicked", {
            "Tab Type": "purchase option description",
            "Tab Name": value
        });
    }, [
        changeTab
    ]);
    const isOptionDisabled = optionType !== "all" && value !== optionType;
    // 다운로드 가능한 경우, 다운로드 표시만 primary로 변경
    const primaryStyle = useCallback((id)=>{
        if (isOptionDisabled) return theme.palette.text.disabled;
        if ((optionType === "download" || value === options.download) && id === 2) return theme.palette.primary.main;
        if (optionType === "all" && value === options.download && id === 2) return theme.palette.primary.main;
        return theme.palette.grey[500];
    }, [
        isOptionDisabled,
        optionType,
        value
    ]);
    const currentTabInfo = value === "download" ? downloadOption : tabOptions[value];
    return /*#__PURE__*/ _jsxs(AllowanceSection, {
        children: [
            /*#__PURE__*/ _jsxs(Box, {
                sx: {
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px"
                },
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "subtitle1",
                        children: isBooks ? "도서" : productType === "basic" ? "구매 옵션별 자료 사용 범위" : "자료 사용 범위"
                    }),
                    /*#__PURE__*/ _jsxs(Box, {
                        sx: {
                            width: "100%",
                            borderRadius: "6px",
                            border: "1px solid ".concat(theme.palette.grey[100])
                        },
                        children: [
                            productType === "basic" ? /*#__PURE__*/ _jsxs(Tabs, {
                                ...tabsProps,
                                onChange: handleClickTab,
                                size: "sm",
                                value: value,
                                sx: {
                                    borderBottom: "1px solid ".concat(theme.palette.grey[100]),
                                    ...theme.typography.subtitle1,
                                    "button:hover": {
                                        color: theme.palette.text.primary
                                    },
                                    "button.Mui-selected": {
                                        color: theme.palette.text.primary
                                    }
                                },
                                children: [
                                    /*#__PURE__*/ _jsx(Tabs.Tab, {
                                        label: tabOptions.view.label,
                                        value: "view",
                                        color: "text.primary",
                                        sx: {
                                            width: "50%",
                                            margin: 0
                                        }
                                    }),
                                    /*#__PURE__*/ _jsx(Tabs.Tab, {
                                        label: downloadOption.label,
                                        value: "download",
                                        sx: {
                                            width: "50%",
                                            margin: 0
                                        }
                                    })
                                ]
                            }) : // freepass type일 경우
                            /*#__PURE__*/ _jsx(Tabs, {
                                ...tabsProps,
                                onChange: handleClickTab,
                                size: "sm",
                                value: value,
                                sx: {
                                    borderBottom: "1px solid ".concat(theme.palette.grey[100])
                                },
                                children: purchaseOptions.map((type)=>/*#__PURE__*/ _jsx(OneTab, {
                                        label: type === "download" ? downloadOption.label : tabOptions[type].label,
                                        value: type,
                                        sx: {
                                            width: "50%"
                                        }
                                    }, type))
                            }),
                            /*#__PURE__*/ _jsxs(InfoBox, {
                                component: "ul",
                                children: [
                                    isOptionDisabled && /*#__PURE__*/ _jsxs(Typography, {
                                        component: "li",
                                        variant: "body1",
                                        color: "status.error",
                                        children: [
                                            /*#__PURE__*/ _jsx(Icon, {
                                                icon: Close_fill,
                                                size: Sizes.xmedium,
                                                color: theme.palette.status.error
                                            }),
                                            "이 자료가 제공하지 않는 옵션이에요."
                                        ]
                                    }),
                                    currentTabInfo.infoText.map((type, idx)=>/*#__PURE__*/ _jsxs(Typography, {
                                            component: "li",
                                            variant: "body1",
                                            color: primaryStyle(idx),
                                            children: [
                                                /*#__PURE__*/ _jsx(Icon, {
                                                    icon: type.icon,
                                                    size: Sizes.xmedium,
                                                    color: primaryStyle(idx)
                                                }),
                                                /*#__PURE__*/ _jsx(HoverLink, {
                                                    href: type.link,
                                                    target: "_blank",
                                                    sx: primaryStyle(idx).includes(theme.palette.primary.main) && {
                                                        ...theme.typography.subtitle1
                                                    },
                                                    children: type.text
                                                })
                                            ]
                                        }, idx))
                                ]
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(Box, {
                color: "grey.500",
                sx: {
                    display: "flex",
                    gap: "5px",
                    p: "6px 8px",
                    i: {
                        pt: "3px"
                    },
                    "&:hover": {
                        borderRadius: "6px",
                        backgroundColor: "rgba(169, 175, 199, 0.20)"
                    }
                },
                children: [
                    /*#__PURE__*/ _jsx(Icon, {
                        icon: Copyright,
                        color: theme.palette.text.secondary,
                        size: Sizes.xsmall
                    }),
                    /*#__PURE__*/ _jsxs(Typography, {
                        component: Link,
                        href: "https://solvookguide.oopy.io/copyright-warning",
                        target: "_blank",
                        variant: "caption",
                        color: "grey.500",
                        sx: (theme)=>({
                                width: "100%",
                                "&:hover": {
                                    color: theme.palette.text.secondary
                                },
                                span: {
                                    textDecoration: "underline",
                                    ...theme.typography.label
                                }
                            }),
                        children: [
                            "해당 자료는 저작권법에 의해 ",
                            /*#__PURE__*/ _jsx("span", {
                                children: "보호받는 저작물"
                            }),
                            "로",
                            " ",
                            /*#__PURE__*/ _jsx("span", {
                                children: "무단전재(제3자전달)와 무단복제를 금합니다."
                            })
                        ]
                    })
                ]
            })
        ]
    });
};
const AllowanceSection = styled("section")((param)=>{
    let { theme  } = param;
    return {
        display: "flex",
        flexDirection: "column",
        gap: "4px"
    };
});
const OneTab = styled(Tabs.Tab)((param)=>{
    let { theme  } = param;
    return {};
});
const InfoBox = styled(Box)((param)=>{
    let { theme  } = param;
    return {
        padding: "20px",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        li: {
            display: "flex",
            gap: "12px",
            i: {
                paddingTop: "2px"
            }
        }
    };
});
const HoverLink = styled(Link)((param)=>{
    let { theme  } = param;
    return {
        ...theme.typography.body1,
        width: "100%",
        color: "inherit",
        "&:hover": {
            color: "inherit",
            textDecoration: "underline"
        }
    };
});
